import React from 'react';
import { TrendingUp, TrendingDown } from 'lucide-react';
import Card from './Card';

/**
 * Little info-card used on the Overview screen.
 * Props API unchanged ({ title, value, icon, trend, color }); `subtitle` is rendered.
 * The hero number spans the full card width (icon sits in an absolute top-right chip) and
 * is sized to fit the tightest 6-across grid at desktop + mobile. Currency is capped at
 * 2 decimal places — never 3.
 */
const formatValue = (value) =>
  typeof value === 'number'
    ? `$${value.toLocaleString(undefined, { maximumFractionDigits: 2 })}`
    : value;

const StatCard = ({ title, value, icon: Icon, trend, subtitle }) => {
  return (
    // Full-height flex column so all six cards are structurally identical: label (fixed
    // height) → hero number on one shared baseline → caption pinned bottom-left.
    <Card className="relative flex flex-col h-full text-left">
      {/* Identical chip on every card — the logo-mark treatment: a soft gold gradient
          rounded square with the Lucide icon in the dark base tone on top (like the Flame
          on gold). Unified gold; no per-metric colors. */}
      {Icon && (
        <span className="absolute top-[22px] right-[22px] flex-shrink-0 inline-flex items-center justify-center w-8 h-8 rounded-arc-sm overflow-hidden shadow-arc-sm">
          <span className="absolute inset-0 arc-grad-bg opacity-85" aria-hidden="true" />
          <Icon className="relative text-[#0B0B0D]/85" size={16} strokeWidth={2} />
        </span>
      )}
      {/* Fixed two-line label area (pr-12 clears the icon) → every hero number aligns. */}
      <p className="min-h-[2.6em] leading-[1.3] text-[11px] font-semibold tracking-[0.04em] uppercase text-arc-ink-4 pr-12">
        {title}
      </p>
      <p className="text-[20px] sm:text-[22px] font-bold tracking-[-0.02em] leading-tight tnum text-arc-ink mt-1.5">
        {formatValue(value)}
      </p>
      {(subtitle || trend !== undefined) && (
        <div className="mt-auto pt-2">
          {subtitle && <p className="text-[11.5px] text-arc-ink-3">{subtitle}</p>}
          {trend !== undefined && (
            <p
              className={`text-sm flex items-center gap-1 mt-1 ${
                trend > 0 ? 'text-arc-pos' : 'text-arc-neg'
              }`}
            >
              {trend > 0 ? <TrendingUp size={16} /> : <TrendingDown size={16} />}
              <span className="tnum">{Math.abs(trend)}%</span>
            </p>
          )}
        </div>
      )}
    </Card>
  );
};

export default StatCard;
